/**
 * Newsletter Overlay Component
 * Landing page overlay for subscribing to the Home CEO newsletter
 * (feature releases and product updates).
 *
 * Under the hood this still calls the `joinWaitlistCallable` Cloud
 * Function — the same email collection is being reframed as a
 * newsletter subscription rather than a waitlist. The backend
 * naming has not been changed alongside this.
 */

import React, { useState } from 'react';
import { FirebaseError } from 'firebase/app';
import { X } from '@phosphor-icons/react';
import { createLogger, trackFormSubmitted } from '@logging';
import { submitWaitlist } from '@lib/waitlist';
import {
  Overlay,
  Card,
  Heading,
  Message,
  Form,
  InputLabel,
  Input,
  SubmitButton,
  CloseButton,
  ErrorText,
  SuccessMessage,
  Links,
  LinkButton,
} from './newsletter-overlay.styles';

const logger = createLogger('NewsletterOverlay');

interface NewsletterOverlayProps {
  onClose: () => void;
}

export const NewsletterOverlay: React.FC<NewsletterOverlayProps> = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    /* Round-trip time for the callable, reported as `duration_ms`. Measured
       from here rather than from the click so it reflects the backend call,
       not how long the visitor spent typing. */
    const startedAt = performance.now();

    try {
      const result = await submitWaitlist({
        email: trimmedEmail,
        displayName: displayName.trim() || undefined,
      });
      setSuccess(true);
      setAlreadySubscribed(result.alreadyOnWaitlist);
      logger.log('Newsletter signup successful');
      /* Form id and outcome only — never the address or the display name.
         `alreadyOnWaitlist` is deliberately not reported either: it is a
         fact about a specific person's prior signup, and the funnel does
         not need it. */
      trackFormSubmitted('waitlist', 'succeeded', performance.now() - startedAt);
    } catch (err) {
      trackFormSubmitted('waitlist', 'failed', performance.now() - startedAt);
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'functions/invalid-argument':
            setError('Please check your email address and try again.');
            break;
          case 'functions/unauthenticated':
          case 'functions/permission-denied':
            /* App Check rejected. Real users almost never see this; if it
               surfaces, it's usually misconfiguration on this client. */
            setError('Submission failed, please reach out to support@homeceo.ai');
            logger.error('App Check rejected:', err);
            break;
          case 'functions/internal':
            setError('Something went wrong. Please try again.');
            break;
          default:
            setError('Unexpected error. Please try again.');
            logger.error('Unknown Firebase error:', err);
        }
      } else {
        setError('Unexpected error. Please try again.');
        logger.error('Non-Firebase error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Card $disableHover style={{ position: 'relative' }}>
        <CloseButton onClick={onClose} aria-label="Close">
          <X size={20} weight="bold" />
        </CloseButton>

        <Heading>Join the Newsletter</Heading>

        {success ? (
          <>
            <SuccessMessage>
              {alreadySubscribed
                ? "You’re already subscribed. We’ll email you as new features ship."
                : "You’re subscribed. We’ll email you as new features ship."}
            </SuccessMessage>
            <Links>
              <LinkButton href="/ai-blogs">Blog</LinkButton>
              <LinkButton href="/legal/contact-us">Contact Us</LinkButton>
            </Links>
          </>
        ) : (
          <>
            <Message>
              New releases, blog posts and major updates
            </Message>

            <Form onSubmit={handleSubmit}>
              <InputLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="required@example.com"
                  required
                  disabled={loading}
                  autoFocus
                />
              </InputLabel>

              <InputLabel>
                <Input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Name"
                  disabled={loading}
                  maxLength={100}
                />
              </InputLabel>

              {error && <ErrorText>{error}</ErrorText>}

              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Subscribing...' : 'Subscribe'}
              </SubmitButton>
            </Form>
          </>
        )}
      </Card>
    </Overlay>
  );
};
